/**
 * `<schmancy-form>` — schmancy form owner with isolated submit state.
 *
 * Architecture:
 * - Extends `SchmancyElement` (shadow DOM, RxJS + `disconnecting` conventions).
 * - Submit state is per-instance and reaches consumers as the `formstate`
 *   event (`bubbles`, `composed`), whose detail is THIS form's state. Bind it
 *   on your own `<schmancy-form>`; there is no shared singleton to read, and
 *   the module-scoped `formSubmitState` is not one (see its doc).
 * - Inner `<form novalidate>` is the native-semantics trigger for Enter-key
 *   submit and `type=submit` button activation.
 * - Fields self-register via `FIELD_CONNECT_EVENT` (composed event from
 *   `FormFieldMixin.connectedCallback`). Stale refs (from disconnected
 *   fields) are filtered by `isConnected` at iteration time.
 *
 * Schema seam — pass any `{ parse(input): T }` object (zod, valibot, ArkType,
 * etc.) via the `schema` property to get typed `submit` event detail.
 */

import { css, html } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { createRef, ref } from 'lit/directives/ref.js'
import { when } from 'lit/directives/when.js'
import { firstValueFrom, from, fromEvent, map, race, takeUntil, timer } from 'rxjs'
import { componentText, SchmancyElement } from '@schmancy/mixins'
import { FIELD_CONNECT_EVENT, type IFormFieldMixin } from '@schmancy/mixins/formField.mixin'
import { deepActiveElement } from '@schmancy/utils/focus'
import { bringFieldIntoView } from './bring-field-into-view'
import { formSubmitState, type FormSubmitState, type FormError } from './form-state'
import '@schmancy/primitives/alert/alert'
import type { SchmancyAlert } from '@schmancy/primitives/alert/alert'

/**
 * Which surface reports a refusal — and they are never both, because a reader
 * shown one fact twice cannot tell whether it is two problems.
 *
 * `summary` is a field-validation failure. Every field that failed is already
 * painting its own message inline, and `bringFieldIntoView` has already put the
 * first of them on screen, so the form adds only the assistive-tech summary in
 * the visually-hidden live region.
 *
 * `alert` is a submission-level refusal — the schema rejecting, or the promise
 * a consumer handed to `detail.until(...)` rejecting. Nothing else on the page
 * is reporting it, which is exactly the hole this closes: before, that message
 * reached the live region and nowhere else, so a sighted user watched the
 * button dim and un-dim and was told nothing at all.
 */
type ErrorSurface = 'summary' | 'alert'

/** Structural type matching zod, valibot, ArkType, etc. — any schema with `.parse()`. */
export interface ParseSchema<T = unknown> {
	parse(input: unknown): T
}

const isButton = (node: EventTarget): node is HTMLElement => {
	if (!(node instanceof HTMLElement)) return false
	return node.tagName === 'BUTTON' || node.tagName === 'SCHMANCY-BUTTON'
}

/**
 * A focused control Enter already operates. On a button, link or summary,
 * Enter's default action is that control's own click, so submitting here too
 * acts twice for one press — Back and Send at once. The rest are controls the
 * user operates rather than types into. Only a text entry has no use of its
 * own for Enter, so only a text entry lends it to implicit submission; a
 * submit button still sends, through its click.
 */
const OPERATED_BY_ENTER = [
	'button',
	'a[href]',
	'summary',
	'select',
	'input:is([type=button],[type=submit],[type=reset],[type=image],[type=checkbox],[type=radio],[type=file],[type=color],[type=range])',
	':is([role=button],[role=link],[role=checkbox],[role=radio],[role=switch],[role=tab],[role=menuitem],[role=menuitemcheckbox],[role=menuitemradio],[role=option],[role=spinbutton])',
].join(',')

/** Submit event detail. `data` is typed when `schema` is set. */
export type SchmancyFormSubmitDetail<T = Record<string, FormDataEntryValue>> = {
	data: T
	formData: FormData
	/**
	 * Register a promise that gates the form's success/error state. If unused,
	 * the form synchronously flips to `success` after dispatch. If used,
	 * `success`/`error` reflect the promise's outcome — and a promise that never
	 * settles at all is answered by `answerTimeout`, which refuses with
	 * `code: 'unanswered'` rather than leaving the control busy forever.
	 */
	until(p: Promise<unknown>): void
}

@customElement('schmancy-form')
export default class SchmancyForm<TSchema extends ParseSchema | undefined = undefined> extends SchmancyElement {
	static styles = css`
		:host {
			display: block;
		}
	`

	public static readonly tagName: string = 'schmancy-form'

	/**
	 * Optional schema for parsing FormData on submit. Anything with a
	 * `.parse(input)` method works (zod, valibot, ArkType). When set, the
	 * `submit` event's `detail.data` is typed `z.infer<TSchema>`.
	 */
	@property({ attribute: false })
	schema?: TSchema

	/** Skip built-in browser constraint validation. Mirrors `<form novalidate>`. */
	@property({ type: Boolean })
	novalidate: boolean = true

	/**
	 * Suppress the form's own visible error alert. For a consumer that already
	 * renders a submission refusal itself — binding `formstate` and painting a
	 * banner where its own layout wants it — and would otherwise show the reader
	 * the same sentence twice.
	 *
	 * Negative, and default-off, because the alert is what every form should
	 * have: the silent form is the defect, so opting out is the exceptional act
	 * and it is the exception that carries the attribute. The visually-hidden
	 * live region is unaffected — suppressing the banner never suppresses the
	 * announcement.
	 */
	@property({ type: Boolean, attribute: 'no-error-alert' })
	noErrorAlert: boolean = false

	/**
	 * How long, in milliseconds, the form waits on the promises a consumer hands
	 * `detail.until(...)` before it stops waiting.
	 *
	 * This is a floor under consumers that forgot to bound their own wait, not
	 * the mechanism by which forms answer. A consumer that cares bounds its own —
	 * there it can say what the act was ("We have not heard back from Revolut"),
	 * and the form cannot: it does not know what it submitted, so all it can
	 * honestly say is the generic sentence below. Reaching this bound means two
	 * bugs, not one: a promise that never settled, and nobody upstream having
	 * bounded the wait on it.
	 *
	 * Which is why the default is far out rather than merely generous. It must
	 * never pre-empt a consumer's own bound — firing first would steal the answer
	 * from a better-worded one that was about to be given — and the bounded waits
	 * in this repo sit at 90 s, against 540 s for the longest server act the
	 * platform will run before killing it. Ten minutes clears both, so when this
	 * fires nothing else was going to speak.
	 *
	 * A value that is not a positive finite number falls back to the default:
	 * there is no "off", because a form that never answers is the defect this
	 * closes, not a mode it offers.
	 */
	@property({ type: Number, attribute: 'answer-timeout' })
	answerTimeout: number = 600_000

	private fields = new Set<IFormFieldMixin>()
	private submitting = false
	private errorAlert = createRef<SchmancyAlert>()

	/**
	 * This form's submit state, and the only authority for it. Per-instance
	 * because a page routinely holds more than one form — an overlay editor
	 * open over the page's own form — and each one's status belongs to it
	 * alone. The live region, `:state(...)`, `aria-busy` and the `formstate`
	 * event detail all read this, so they cannot disagree with each other or
	 * with another form.
	 */
	@state() private submitState: FormSubmitState = { status: 'idle', error: null, submitCount: 0 }
	/** Which surface the current refusal is being reported on — see `ErrorSurface`. */
	@state() private errorSurface: ErrorSurface = 'alert'
	private internals: ElementInternals | undefined = (() => {
		try {
			return this.attachInternals()
		} catch {
			return undefined
		}
	})()

	override connectedCallback(): void {
		super.connectedCallback()

		if (this.parentElement?.closest('schmancy-form')) {
			console.error('[schmancy-form] nested <schmancy-form> is not supported')
			return
		}

		// Field registry — composed event from FormFieldMixin.connectedCallback.
		fromEvent<CustomEvent<IFormFieldMixin>>(this, FIELD_CONNECT_EVENT)
			.pipe(takeUntil(this.disconnecting))
			.subscribe(e => this.fields.add(e.detail))

		// Submit-trigger interception — slotted descendants live in light DOM
		// while the inner <form> is in shadow DOM, so native form-association
		// across the shadow boundary doesn't work for `<button type=submit>`.
		// Capture clicks on type=submit buttons (native + schmancy-button) and
		// Enter from a text entry, then call the inner form's
		// requestSubmit() which fires the same native submit event handler
		// chain that a directly-associated button would have triggered.
		fromEvent<MouseEvent>(this, 'click')
			.pipe(takeUntil(this.disconnecting))
			.subscribe(e => this.maybeRequestSubmit(e))
		fromEvent<KeyboardEvent>(this, 'keydown')
			.pipe(takeUntil(this.disconnecting))
			.subscribe(e => {
				if (e.key !== 'Enter' || e.shiftKey) return
				// `e.target` is useless here: this listener is on the host, so the
				// event arrives retargeted to the outermost node of the composed
				// path in this tree — the field host for a <schmancy-textarea>,
				// the consumer's host for a field nested deeper. Never the
				// <textarea>. composedPath()[0] is the node the key reached, and
				// index 0 alone suffices: nothing can nest inside a <textarea>,
				// and isContentEditable is inherited, so the deepest node in an
				// editable region reports it too.
				//
				// Suppress only where Enter is about to insert a newline. A field
				// handler is nearer the target than this one, so a field that
				// consumed Enter has already called preventDefault() by now and no
				// newline is coming — which is how a single-line editable field
				// earns the Enter-submits behaviour a native <input> has. A field
				// that wants Enter entirely to itself stops propagation instead,
				// and this listener never runs.
				const source = e.composedPath()[0]
				const insertsNewline =
					source instanceof HTMLElement &&
					(source.tagName === 'TEXTAREA' || source.isContentEditable) &&
					!e.defaultPrevented
				if (insertsNewline) return
				if (source instanceof Element && source.matches(OPERATED_BY_ENTER)) return
				this.maybeRequestSubmit(e)
			})
	}

	private maybeRequestSubmit(e: Event): void {
		// On click: trigger only when the target is a type=submit button.
		// On keydown(Enter): always trigger — an Enter that inserts a newline or
		// operates the focused control is never passed here.
		// type=reset is handled separately in maybeReset.
		if (e.type === 'click') {
			const path = e.composedPath()
			const resetBtn = path.find(node => isButton(node) && node.getAttribute('type') === 'reset')
			if (resetBtn) {
				e.preventDefault()
				const form = this.shadowRoot?.querySelector('form')
				form?.reset()
				return
			}
			const submitBtn = path.find(node => isButton(node) && node.getAttribute('type') === 'submit')
			if (!submitBtn) return
			e.preventDefault()
		}
		const form = this.shadowRoot?.querySelector('form')
		form?.requestSubmit()
	}

	/** Active fields — drops stale refs from disconnected nodes. */
	private get activeFields(): IFormFieldMixin[] {
		return [...this.fields].filter(f => f.isConnected)
	}

	/** The control the reader submitted from — the submit button pressed. Its
	 *  refusal may take focus from it and from nowhere else: fields stay
	 *  focusable while a submission is out, and a reader correcting another one
	 *  must not have it blurred by a refusal that lands late. A submission by
	 *  Enter records nothing, because `<schmancy-input>` blurs itself on Enter
	 *  before this handler runs; that refusal takes focus from nothing. */
	private submittedFrom: Element | null = null

	private async onSubmit(e: SubmitEvent): Promise<void> {
		e.preventDefault()
		e.stopPropagation()
		if (this.submitting) return
		const focused = deepActiveElement()
		this.submittedFrom = focused === document.body ? null : focused

		// If any field has an async validator pending, wait for them all to
		// settle before deciding validity. This is the hard-block model: the
		// user clicks submit, the form holds (aria-busy="true"), waits for
		// validators, then proceeds with the truth they reported.
		const pendingValidators = this.activeFields.filter(f => f.isValidating)
		if (pendingValidators.length > 0) {
			this.broadcastStatus('submitting')
			await new Promise<void>(resolve => {
				const tick = () => {
					if (this.activeFields.every(f => !f.isValidating)) resolve()
					else requestAnimationFrame(tick)
				}
				tick()
			})
		}

		// Every attempt counts, refused ones included — a refusal IS an attempt,
		// and `submitCount` is how a consumer tells "never submitted" from
		// "submitted and bounced". Incremented once here rather than on the
		// path that happens to succeed.
		this.submitState = { ...this.submitState, submitCount: this.submitState.submitCount + 1 }

		// Phase 4 — submit forces error display on every field.
		this.activeFields.forEach(f => f.markSubmitted())

		// `map(...).every(...)`, never `every(f => f.checkValidity())`: `every`
		// short-circuits on the first `false`, so every field after the first
		// invalid one is left unvalidated and its `error` flag stays whatever it
		// was. Counting invalid fields below reads those flags, and a form with
		// five empty required fields would count one.
		const allValid = this.activeFields.map(f => f.checkValidity()).every(Boolean)
		if (!allValid) {
			// One invalid field already has the sentence its author wrote for it;
			// the anonymous plural is what several of them collapse to, and is
			// strictly worse than the specific one when there is only one. A field
			// that raises `error` while rendering its words somewhere else leaves
			// `validationMessage` empty (see formField.mixin.ts) — nothing to say,
			// so it takes the summary too.
			const invalid = this.activeFields.filter(f => f.error)
			const announcement =
				invalid.length === 1 && invalid[0].validationMessage
					? invalid[0].validationMessage
					: 'Validation failed. Please correct the highlighted fields.'
			this.broadcastStatus('error', { message: announcement }, 'summary')
			bringFieldIntoView(invalid[0] as unknown as HTMLElement | undefined)
			return
		}

		this.submitting = true
		try {
			const formData = new FormData()
			for (const field of this.activeFields) {
				for (const [k, v] of field.toFormEntries()) formData.append(k, v)
			}
			const raw = Object.fromEntries(formData)
			const data = this.schema ? this.schema.parse(raw) : raw
			this.broadcastStatus('submitting')

			const pending: Promise<unknown>[] = []
			this.dispatchEvent(
				new CustomEvent<SchmancyFormSubmitDetail<unknown>>('submit', {
					detail: {
						data,
						formData,
						until: (p: Promise<unknown>) => pending.push(p),
					},
				}),
			)

			// What it knows is that no answer arrived — never that the act failed.
			// It cannot tell a save that was refused from one that succeeded
			// silently, and a form that may have just moved money telling the
			// reader it failed when it may have worked is worse than telling them
			// nothing. `code` is how a consumer tells this from a real rejection.
			const answered = await firstValueFrom(
				race(
					from(Promise.all(pending)).pipe(map(() => true)),
					timer(this.answerTimeout > 0 && Number.isFinite(this.answerTimeout) ? this.answerTimeout : 600_000).pipe(
						map(() => false),
					),
				),
			)
			if (!answered) {
				this.broadcastStatus('error', {
					message: componentText('form.unanswered'),
					code: 'unanswered',
				})
				return
			}
			this.broadcastStatus('success')
		} catch (err) {
			const messages =
				typeof err === 'object' && err !== null && 'issues' in err && Array.isArray(err.issues)
					? err.issues.flatMap(issue =>
							typeof issue === 'object' &&
							issue !== null &&
							'message' in issue &&
							typeof issue.message === 'string' &&
							issue.message.trim()
								? [issue.message]
								: [],
						)
					: []
			this.broadcastStatus('error', {
				message: messages.join('\n') || (err instanceof Error ? err.message : String(err)),
			})
		} finally {
			this.submitting = false
		}
	}

	private onReset(e: Event): void {
		e.stopPropagation()
		this.activeFields.forEach(f => f.resetForm())
		// A reset returns the form to never-submitted, count included.
		this.submitState = { status: 'idle', error: null, submitCount: 0 }
		this.broadcastStatus('idle')
		this.dispatchEvent(new CustomEvent('reset'))
	}

	/**
	 * Advance this form's submit state and announce it — the single author, so
	 * every surface reporting the state reports the same one.
	 *
	 * `error` carries the message the operator is shown, not a shorter code for
	 * the state and a longer sentence for the live region: a consumer's visible
	 * banner and the assistive-tech announcement describe one refusal, and two
	 * strings for it can only drift.
	 *
	 * `surface` picks which of the two reporting surfaces carries this refusal,
	 * and raising/clearing the alert happens here rather than at the call sites
	 * for the same reason the status itself is authored here: one writer, so the
	 * banner, the live region, the custom states and the event cannot disagree.
	 */
	private broadcastStatus(
		status: FormSubmitState['status'],
		error: FormError | null = null,
		surface: ErrorSurface = 'alert',
	): void {
		this.submitState = { ...this.submitState, status, error }
		this.errorSurface = surface
		const states = this.internals?.states
		if (states) {
			for (const s of ['submitting', 'success', 'error', 'idle']) states.delete(s)
			states.add(status)
		}
		// Every other status clears it, the next submit attempt included — a
		// refusal the reader is about to supersede must not sit above the form
		// while its replacement is in flight.
		//
		// After the render the state write above just scheduled: the alert's ref
		// resolves on this form's first render, and `setFormError()` is a public
		// method a consumer can reach before one has happened.
		void this.updateComplete.then(() => {
			if (status === 'error' && surface === 'alert' && !this.noErrorAlert && error) {
				if (this.errorAlert.value) this.errorAlert.value.reportsOn = this.submittedFrom
				this.errorAlert.value?.raise({ tone: 'error', headline: error.message })
			} else {
				this.errorAlert.value?.clear()
			}
		})
		// aria-busy on the host while submitting (WCAG 2.2 AA — disabled buttons
		// drop from tab order; keep them focusable, signal busy via aria).
		if (status === 'submitting') this.setAttribute('aria-busy', 'true')
		else this.removeAttribute('aria-busy')
		// Legacy mirror. `formSubmitState` is module-scoped, so this overwrites
		// whatever another form last wrote — which is exactly why it is not the
		// channel and nothing here reads it back. Kept written so an external
		// consumer that reached for it before `formstate` existed still sees a
		// value; derived from `submitState`, never an independent authority.
		formSubmitState.set({ ...this.submitState })
		// The channel. Scoped to this form, so a consumer binding it on their
		// own <schmancy-form> hears that form and no other.
		this.dispatchEvent(
			new CustomEvent<FormSubmitState>('formstate', {
				detail: { ...this.submitState },
				bubbles: true,
				composed: true,
			}),
		)
	}

	/**
	 * Server-side error mapping (RHF `setError(name, ...)`-equivalent).
	 * Sets `setCustomValidity(message)` on the matching field and ensures the
	 * error displays by marking it submitted.
	 */
	public setFieldError(name: string, message: string): boolean {
		const field = this.activeFields.find(f => f.name === name)
		if (!field) return false
		field.setCustomValidity(message)
		field.markSubmitted()
		return true
	}

	/**
	 * Top-of-form error (RHF `setError('root.serverError', ...)`-equivalent).
	 * Flips form status to 'error' with a structured `FormError`.
	 */
	public setFormError(message: string, code?: string): void {
		this.broadcastStatus('error', { message, code })
	}

	/**
	 * Clear the `submitted` flag on every registered field without resetting
	 * their values. Wizard pattern: stepping back from a later step should not
	 * leave the earlier step's fields in aggressive "show all errors" mode
	 * (which `submitted = true` triggers via the `shouldShowError()` gate).
	 *
	 * Pristine fields with `validateOn: 'dirty'` go quiet again. Fields the
	 * user actually dirtied keep showing their errors (correct UX — those are
	 * still genuine mistakes the user can see).
	 */
	public clearSubmitted(): void {
		this.activeFields.forEach(f => f.clearSubmitted())
		this.broadcastStatus('idle')
	}

	/** Programmatically submit via the native submitter pipeline. */
	public submit(): boolean {
		const form = this.shadowRoot?.querySelector('form')
		if (!form) return false
		form.requestSubmit()
		return true
	}

	/** Programmatically reset via native `form.reset()`. */
	public reset(): void {
		const form = this.shadowRoot?.querySelector('form')
		form?.reset()
	}

	/**
	 * Short-circuiting is the platform's own behaviour here: native
	 * `form.reportValidity()` reports the FIRST invalid control and stops.
	 */
	public reportValidity(): boolean {
		return this.activeFields.every(f => f.reportValidity())
	}

	/**
	 * Every field, not `every(f => f.checkValidity())`: a field's
	 * `checkValidity()` also paints its own error state, and native
	 * `form.checkValidity()` runs the check on all controls — short-circuiting
	 * would mark only the first invalid one.
	 */
	public checkValidity(): boolean {
		return this.activeFields.map(f => f.checkValidity()).every(Boolean)
	}

	/** Snapshot of current form values from the registered fields. */
	public getFormData(): FormData {
		const formData = new FormData()
		for (const field of this.activeFields) {
			for (const [k, v] of field.toFormEntries()) formData.append(k, v)
		}
		return formData
	}

	render() {
		return html`
			<form class=${this.className} ?novalidate=${this.novalidate} @submit=${this.onSubmit} @reset=${this.onReset}>
				<!--
					The form's own visible refusal, at the top of the form: the standard
					error-summary position, and the only one the form can pick without
					knowing where in the slotted content the action sits. Raised in the
					error tone, it is announced as an alert, and takes focus once it
					settles if focus is still on the control the reader submitted from.
					Unraised it costs no box, and no flex/grid gap in the class the
					consumer put on this form.

					No anchor is set: an anchor is the thing that must stay in view while
					the panel grows, and for a panel that opens ABOVE the content that is
					the panel itself, which is the alert's own fallback. Its reportsOn is
					set at the raise, to submittedFrom.
				-->
				${when(!this.noErrorAlert, () => html`<schmancy-alert ${ref(this.errorAlert)}></schmancy-alert>`)}
				<slot></slot>
			</form>
			<!--
				Form-level live region — visually hidden via the .sr-only convention;
				WCAG 4.1.3 (Status Messages). It carries every refusal EXCEPT the one
				the alert above is itself announcing: two live regions holding one
				sentence announce it twice. The alert raises a refusal in the error
				tone, whose panel carries role="alert" when the page was reachable as
				it was inserted, and whose own assertive region says it when the page
				was not. So a validation summary lands here always, and a
				submission refusal lands here exactly when the alert is suppressed —
				dropping the banner must never drop the announcement with it.
			-->
			<div role="status" aria-live="assertive" aria-atomic="true" class="sr-only">
				${this.submitState.status === 'error' && (this.errorSurface === 'summary' || this.noErrorAlert)
					? (this.submitState.error?.message ?? '')
					: ''}
			</div>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-form': SchmancyForm
	}
}

// Retained type surfaces — kept exported because downstream code may import
// them as documentation. The new implementation no longer uses them
// internally.

export interface FormElement extends HTMLElement {
	name?: string
	value?: string
	disabled?: boolean
	type?: string
	defaultValue?: string
}

export interface CheckableFormElement extends FormElement {
	checked?: boolean
}

export interface ValidatableFormElement extends FormElement {
	reportValidity?: () => boolean
	checkValidity?: () => boolean
}

export interface FormEventMap {
	submit: CustomEvent<SchmancyFormSubmitDetail<unknown>>
	reset: CustomEvent
	formstate: CustomEvent<FormSubmitState>
}
